class LeAmiketteHero extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') {
      this.pageHidden = document.hidden;
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.start();
      return;
    }
    this.dataset.initialized = 'true';
    this.slides = [...this.querySelectorAll('[data-la-slide]')];
    this.dots = [...this.querySelectorAll('[data-la-dot]')];
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.delay = Number(this.dataset.autoplaySpeed || 5) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;
    this.paused = false;
    this.userPaused = false;
    this.pageHidden = document.hidden;
    this.videoLoadingEnabled = false;
    this.pointerStart = null;
    this.currentLabel = this.querySelector('[data-la-hero-current]');
    this.toggleButton = this.querySelector('[data-la-hero-toggle]');
    this.progressBar = this.querySelector('[data-la-hero-progress]');
    this.scrollButton = this.querySelector('[data-la-hero-scroll]');
    this.onVisibilityChange = () => {
      this.pageHidden = document.hidden;
      if (this.pageHidden) window.clearInterval(this.timer);
      else this.start();
    };

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.show(index, true));
    });
    this.querySelector('[data-la-hero-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    this.querySelector('[data-la-hero-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.toggleButton?.addEventListener('click', () => this.toggleAutoplay());
    this.scrollButton?.addEventListener('click', () => this.scrollToNextSection());

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume();
    });
    this.addEventListener('pointerdown', (event) => {
      this.pointerStart = event.clientX;
      this.pause();
    });
    this.addEventListener('pointerup', (event) => {
      if (this.pointerStart !== null) {
        const delta = event.clientX - this.pointerStart;
        if (Math.abs(delta) > 45) this.show(this.index + (delta < 0 ? 1 : -1), true);
      }
      this.pointerStart = null;
      this.resume();
    });
    this.addEventListener('pointercancel', () => {
      this.pointerStart = null;
      this.resume();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.show(this.index + 1, true);
      if (event.key === 'ArrowLeft') this.show(this.index - 1, true);
    });

    this.show(this.index, false);
    this.updateAutoplayControl();
    this.start();
    this.scheduleVideoLoad();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  show(nextIndex, userInitiated = false) {
    if (!this.slides.length) return;
    this.index = (nextIndex + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('inert', !active);
      slide.querySelectorAll('[data-la-hero-video]').forEach((video) => {
        if (active && !this.reduceMotion) {
          if (this.videoLoadingEnabled) this.loadVideo(video);
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });

    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.setAttribute('aria-label', `${dot.dataset.label || 'Slide'} ${index + 1}`);
    });

    if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');

    this.restartProgress();

    if (userInitiated) this.restart();
  }

  start() {
    if (!this.autoplay || this.userPaused || this.pageHidden || this.slides.length < 2 || this.paused) return;
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
    this.restartProgress();
  }

  pause() {
    this.paused = true;
    window.clearInterval(this.timer);
    if (this.progressBar) this.progressBar.style.animationPlayState = 'paused';
  }

  resume() {
    this.paused = false;
    if (this.progressBar) this.progressBar.style.animationPlayState = 'running';
    this.start();
  }

  restart() {
    window.clearInterval(this.timer);
    if (!this.paused) this.start();
  }

  toggleAutoplay() {
    this.userPaused = !this.userPaused;
    this.updateAutoplayControl();
    if (this.userPaused) window.clearInterval(this.timer);
    else this.start();
  }

  updateAutoplayControl() {
    if (!this.toggleButton) return;
    const isPaused = this.userPaused || !this.autoplay;
    this.toggleButton.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
    this.toggleButton.setAttribute(
      'aria-label',
      isPaused ? 'Riprendi la presentazione' : 'Metti in pausa la presentazione'
    );
    this.toggleButton.querySelector('[data-la-pause-icon]')?.toggleAttribute('hidden', isPaused);
    this.toggleButton.querySelector('[data-la-play-icon]')?.toggleAttribute('hidden', !isPaused);
    this.classList.toggle('is-user-paused', isPaused);
  }

  restartProgress() {
    if (!this.progressBar || !this.autoplay || this.userPaused || this.reduceMotion) return;
    this.progressBar.style.animation = 'none';
    void this.progressBar.offsetWidth;
    this.progressBar.style.removeProperty('animation');
    this.progressBar.style.animationPlayState = this.paused ? 'paused' : 'running';
  }

  scrollToNextSection() {
    const section = this.closest('.shopify-section');
    const nextSection = section?.nextElementSibling;
    nextSection?.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  scheduleVideoLoad() {
    if (this.reduceMotion || navigator.connection?.saveData) return;
    if (['slow-2g', '2g', '3g'].includes(navigator.connection?.effectiveType)) return;

    // The first hero video is above the fold: start it immediately instead of
    // waiting for window.load or an idle callback. Only the active slide is
    // loaded, so hidden slides keep their deferred sources.
    this.videoLoadingEnabled = true;
    this.loadDeferredVideos();
  }

  loadDeferredVideos() {
    this.slides[this.index]?.querySelectorAll('[data-la-hero-video]').forEach((video) => this.loadVideo(video));
  }

  loadVideo(video) {
    if (!video || video.dataset.loaded === 'true') return;
    const mobileViewport = window.matchMedia('(max-width: 989px)').matches;
    const mobileOnly = video.hasAttribute('data-la-mobile-hero-video');
    if (mobileOnly && !mobileViewport) return;
    if (!mobileOnly && video.classList.contains('la-hero__video--desktop') && mobileViewport) return;

    const sources = [...video.querySelectorAll('source[data-src]')];
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    if (!sources.length) return;
    video.dataset.loaded = 'true';
    video.addEventListener('canplay', () => video.classList.add('is-ready'), { once: true });
    video.load();
    if (video.closest('[data-la-slide]')?.classList.contains('is-active')) {
      video.play().catch(() => {});
    }
  }
}

if (!customElements.get('leamikette-hero')) {
  customElements.define('leamikette-hero', LeAmiketteHero);
}

class LeAmiketteProductTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.tabs = [...this.querySelectorAll('[data-la-product-tab]')];
    this.panels = [...this.querySelectorAll('[data-la-product-panel]')];

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        this.select(next, true);
      });
    });
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });
    this.panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });
  }
}

if (!customElements.get('leamikette-product-tabs')) {
  customElements.define('leamikette-product-tabs', LeAmiketteProductTabs);
}

class LeAmiketteCategoryCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.viewport = this.querySelector('[data-la-category-viewport]');
    this.cards = [...this.querySelectorAll('[data-la-category-card]')];
    this.previousButton = this.closest('section')?.querySelector('[data-la-category-previous]');
    this.nextButton = this.closest('section')?.querySelector('[data-la-category-next]');
    this.currentLabel = this.closest('section')?.querySelector('[data-la-category-current]');
    this.progress = this.querySelector('[data-la-category-progress]');

    if (!this.viewport || !this.cards.length) return;

    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.nextButton?.addEventListener('click', () => this.move(1));
    this.cards.forEach((card, index) => {
      card.addEventListener('mouseenter', () => this.setCurrent(index));
      card.addEventListener('focusin', () => this.setCurrent(index));
    });
    this.viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      }
    });
    this.viewport.addEventListener(
      'scroll',
      () => {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.update());
      },
      { passive: true }
    );

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.viewport);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.frame);
  }

  move(direction) {
    const currentIndex = this.getClosestCardIndex();
    const nextIndex = Math.max(0, Math.min(this.cards.length - 1, currentIndex + direction));
    this.cards[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  getClosestCardIndex() {
    const viewportStart = this.viewport.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - viewportStart);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  update() {
    const currentIndex = this.getClosestCardIndex();
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const scrollProgress = maxScroll > 0 ? this.viewport.scrollLeft / maxScroll : 1;
    const progressStart = 1 / this.cards.length;
    const progressScale = progressStart + scrollProgress * (1 - progressStart);

    this.setCurrent(currentIndex);
    if (this.progress) this.progress.style.transform = `scaleX(${Math.max(0, Math.min(1, progressScale))})`;
    if (this.previousButton) this.previousButton.disabled = this.viewport.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 2;
  }

  setCurrent(currentIndex) {
    this.cards.forEach((card, index) => {
      const isCurrent = index === currentIndex;
      card.classList.toggle('is-current', isCurrent);
      card.classList.toggle('is-before', index < currentIndex);
      card.classList.toggle('is-after', index > currentIndex);
      card.toggleAttribute('data-current', isCurrent);
    });
    if (this.currentLabel) this.currentLabel.textContent = String(currentIndex + 1).padStart(2, '0');
  }
}

if (!customElements.get('leamikette-category-carousel')) {
  customElements.define('leamikette-category-carousel', LeAmiketteCategoryCarousel);
}

const enforceDrawerNavigation = () => {
  const header = document.querySelector('#header-component');
  if (header && header.dataset.menuStyle !== 'drawer') header.dataset.menuStyle = 'drawer';
};

const configureFooterAccordions = (root = document) => {
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  root.querySelectorAll('[data-la-footer-details]').forEach((details) => {
    details.toggleAttribute('open', !isMobile);
  });
};

const leAmiketteMotion = {
  reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  revealObserver: null,
  parallaxElements: new Set(),
  parallaxFrame: null,

  initialize(root = document) {
    this.setupReveals(root);
    this.setupParallax(root);
    this.setupCategoryDepth(root);
    this.setupProductDepth(root);
    this.setupDrawerExperience(root);
    this.setupMegaMenuPreview(root);
    this.setupCursor(root);
    this.setupCartCelebration(root);
  },

  setupReveals(root) {
    const automaticSelectors = [
      '.la-section-heading',
      '.la-editorial__media',
      '.la-instagram__header',
      '.la-newsletter__inner',
      '.la-review-card',
    ];

    root.querySelectorAll(automaticSelectors.join(',')).forEach((element) => {
      if (!element.hasAttribute('data-la-reveal')) element.dataset.laReveal = 'section';
    });

    const revealItems = [...root.querySelectorAll('[data-la-reveal]:not([data-la-reveal-ready])')];
    revealItems.forEach((element, index) => {
      element.dataset.laRevealReady = 'true';
      element.style.setProperty('--la-reveal-order', index % 6);
    });

    if (this.reduced || !('IntersectionObserver' in window)) {
      revealItems.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    document.documentElement.classList.add('la-motion-ready');
    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -42px' }
      );
    }
    revealItems.forEach((element) => this.revealObserver.observe(element));
  },

  setupParallax(root) {
    root.querySelectorAll('[data-la-parallax], [data-la-hero-parallax]').forEach((element) => {
      this.parallaxElements.add(element);
    });
    if (this.reduced || this.parallaxBound) return;
    this.parallaxBound = true;
    const requestRender = () => {
      if (this.parallaxFrame) return;
      this.parallaxFrame = window.requestAnimationFrame(() => this.renderParallax());
    };
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
    requestRender();
  },

  renderParallax() {
    this.parallaxFrame = null;
    const viewportHeight = window.innerHeight;
    this.parallaxElements.forEach((element) => {
      if (!element.isConnected) {
        this.parallaxElements.delete(element);
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > viewportHeight + 100) return;
      const distance = viewportHeight / 2 - (rect.top + rect.height / 2);
      const shift = Math.max(-34, Math.min(34, distance * 0.055));
      const property = element.hasAttribute('data-la-hero-parallax') ? '--la-hero-parallax' : '--la-parallax-y';
      element.style.setProperty(property, `${shift.toFixed(1)}px`);
    });
  },

  setupCategoryDepth(root) {
    if (this.reduced || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    root.querySelectorAll('.la-category-card > a:not([data-la-depth-ready])').forEach((card) => {
      card.dataset.laDepthReady = 'true';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top) / rect.height;
        card.style.setProperty('--la-pointer-x', `${(x * 100).toFixed(1)}%`);
        card.style.setProperty('--la-pointer-y', `${(y * 100).toFixed(1)}%`);
        card.style.setProperty('--la-tilt-x', `${((0.5 - y) * 5).toFixed(2)}deg`);
        card.style.setProperty('--la-tilt-y', `${((x - 0.5) * 6).toFixed(2)}deg`);
        card.style.setProperty('--la-image-x', `${((0.5 - x) * 8).toFixed(1)}px`);
        card.style.setProperty('--la-image-y', `${((0.5 - y) * 8).toFixed(1)}px`);
      });
      card.addEventListener('pointerleave', () => {
        ['--la-tilt-x', '--la-tilt-y', '--la-image-x', '--la-image-y'].forEach((property) => card.style.removeProperty(property));
      });
    });
  },

  setupProductDepth(root) {
    if (this.reduced || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    root.querySelectorAll('.la-product-card:not([data-la-product-depth-ready])').forEach((card) => {
      card.dataset.laProductDepthReady = 'true';
      const visual = card.querySelector('.la-product-card__visual');
      if (!visual) return;
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top) / rect.height;
        visual.style.setProperty('--la-product-tilt-x', `${((0.5 - y) * 3.2).toFixed(2)}deg`);
        visual.style.setProperty('--la-product-tilt-y', `${((x - 0.5) * 3.8).toFixed(2)}deg`);
        visual.style.setProperty('--la-product-light-x', `${(x * 100).toFixed(1)}%`);
        visual.style.setProperty('--la-product-light-y', `${(y * 100).toFixed(1)}%`);
      });
      card.addEventListener('pointerleave', () => {
        ['--la-product-tilt-x', '--la-product-tilt-y', '--la-product-light-x', '--la-product-light-y'].forEach((property) => {
          visual.style.removeProperty(property);
        });
      });
    });
  },

  setupDrawerExperience(root) {
    root.querySelectorAll('.menu-drawer-container:not([data-la-menu-motion-ready])').forEach((container) => {
      container.dataset.laMenuMotionReady = 'true';
      const syncState = () => {
        const open = container.hasAttribute('open');
        container.toggleAttribute('data-la-menu-open', open);
        if (!open || this.reduced) return;
        const menu = container.querySelector('.la-mega-menu');
        if (!menu) return;
        menu.classList.remove('la-mega-menu--entering');
        window.requestAnimationFrame(() => menu.classList.add('la-mega-menu--entering'));
      };
      new MutationObserver(syncState).observe(container, { attributes: true, attributeFilter: ['open'] });
      syncState();
    });
  },

  setupMegaMenuPreview(root) {
    root.querySelectorAll('.la-mega-menu:not([data-la-preview-ready])').forEach((menu) => {
      menu.dataset.laPreviewReady = 'true';
      const cta = menu.querySelector('[data-la-mega-cta]');
      const ctaLabel = menu.querySelector('[data-la-mega-cta-label]');
      const previews = [...menu.querySelectorAll('.la-mega-menu__preview')];

      const activate = (link) => {
        const previewName = link.dataset.preview;
        if (!previewName) return;
        menu.dataset.activePreview = previewName;
        previews.forEach((preview) => {
          preview.classList.toggle('is-active', preview.classList.contains(`la-mega-menu__preview--${previewName}`));
        });
        if (cta) cta.href = link.href;
        if (ctaLabel) ctaLabel.textContent = link.dataset.ctaLabel || link.textContent.trim();
      };

      menu.querySelectorAll('.la-mega-menu__link[data-preview]').forEach((link) => {
        link.addEventListener('pointerenter', () => activate(link));
        link.addEventListener('focusin', () => activate(link));
      });
    });
  },

  setupCursor(root) {
    if (this.reduced || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (!this.cursor) {
      this.cursor = document.createElement('div');
      this.cursor.className = 'la-effect-cursor';
      this.cursor.setAttribute('aria-hidden', 'true');
      document.body.append(this.cursor);
      document.addEventListener('pointermove', (event) => {
        this.cursorX = event.clientX;
        this.cursorY = event.clientY;
        if (this.cursorFrame) return;
        this.cursorFrame = window.requestAnimationFrame(() => {
          this.cursor.style.transform = `translate3d(${this.cursorX - 34}px, ${this.cursorY - 34}px, 0) scale(${this.cursor.classList.contains('is-visible') ? 1 : 0.65})`;
          this.cursorFrame = null;
        });
      });
    }
    root.querySelectorAll('[data-la-cursor]:not([data-la-cursor-ready])').forEach((element) => {
      element.dataset.laCursorReady = 'true';
      element.addEventListener('pointerenter', () => {
        this.cursor.textContent = element.dataset.laCursor || 'SCOPRI';
        this.cursor.classList.add('is-visible');
      });
      element.addEventListener('pointerleave', () => this.cursor.classList.remove('is-visible'));
    });
  },

  setupCartCelebration(root) {
    root.querySelectorAll('.add-to-cart-button:not([data-la-cart-effect-ready])').forEach((button) => {
      button.dataset.laCartEffectReady = 'true';
      button.addEventListener('click', () => {
        button.classList.remove('la-add-pulse');
        window.requestAnimationFrame(() => button.classList.add('la-add-pulse'));

        if (!this.reduced) {
          const card = button.closest(
            '.la-product-card, product-information, .product-information, [data-product-id], [data-template^="product"]'
          );
          const sourceImage =
            card?.querySelector('.la-product-card__image--primary, .product-media img, img') ||
            document.querySelector('[data-template^="product"] .product-media img');
          const cartTarget = document.querySelector(
            '.header-actions__cart-icon, .header-actions a[href*="/cart"], button[aria-label*="Carrello" i]'
          );
          if (sourceImage && cartTarget && typeof sourceImage.animate === 'function') {
            const sourceRect = sourceImage.getBoundingClientRect();
            const targetRect = cartTarget.getBoundingClientRect();
            const size = Math.max(46, Math.min(72, sourceRect.width * 0.22));
            const flight = document.createElement('span');
            const image = sourceImage.cloneNode(false);
            flight.className = 'la-cart-flight';
            flight.setAttribute('aria-hidden', 'true');
            flight.style.cssText = `--la-flight-size:${size}px;left:${sourceRect.left + sourceRect.width / 2 - size / 2}px;top:${sourceRect.top + sourceRect.height / 2 - size / 2}px;`;
            flight.append(image);
            document.body.append(flight);

            const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
            const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);
            const animation = flight.animate(
              [
                { opacity: 0, transform: 'translate3d(0, 10px, 0) scale(.72) rotate(-8deg)' },
                { opacity: 1, offset: 0.18, transform: 'translate3d(0, 0, 0) scale(1) rotate(0)' },
                { opacity: 1, offset: 0.62, transform: `translate3d(${deltaX * 0.58}px, ${deltaY * 0.36 - 46}px, 0) scale(.72) rotate(10deg)` },
                { opacity: 0, transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(.18) rotate(20deg)` },
              ],
              { duration: 720, easing: 'cubic-bezier(.2,.72,.2,1)', fill: 'forwards' }
            );
            animation.finished.finally(() => flight.remove());
          }
        }

        window.setTimeout(() => {
          const bubble = document.querySelector('.header-actions .cart-bubble, .header-actions__cart-icon .cart-bubble');
          if (!bubble) return;
          bubble.classList.remove('la-cart-celebrate');
          window.requestAnimationFrame(() => bubble.classList.add('la-cart-celebrate'));
        }, 520);
        window.setTimeout(() => button.classList.remove('la-add-pulse'), 760);
      });
    });
  },
};

document.addEventListener('DOMContentLoaded', () => {
  enforceDrawerNavigation();
  configureFooterAccordions();
  leAmiketteMotion.initialize();
  const header = document.querySelector('#header-component');
  if (header) {
    new MutationObserver(enforceDrawerNavigation).observe(header, {
      attributes: true,
      attributeFilter: ['data-menu-style'],
    });
  }
});

window.matchMedia('(max-width: 749px)').addEventListener('change', () => configureFooterAccordions());

document.addEventListener('shopify:section:load', (event) => {
  enforceDrawerNavigation();
  configureFooterAccordions(event.target);
  leAmiketteMotion.initialize(event.target);
});
